import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "../ui/use-toast";
import { PhotoUploadField } from "./profile/PhotoUploadField";
import { SkillsInput } from "./profile/SkillsInput";
import { ProfileBasicInfo } from "./profile/ProfileBasicInfo";
import { ProfileLinks } from "./profile/ProfileLinks";
import { ProfileBio } from "./profile/ProfileBio";

import { useProfile } from "@/hooks/useProfile";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Mail } from "lucide-react";

interface EditProfileDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditProfileDialog({
  open,
  onOpenChange,
}: EditProfileDialogProps) {
  const [description, setDescription] = useState("");
  const [skills, setSkills] = useState<string[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [fullName, setFullName] = useState("");
  const [headline, setHeadline] = useState("");
  const [location, setLocation] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [githubUrl, setGithubUrl] = useState("");
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const { toast } = useToast();
  
  const { invalidateProfile } = useProfile();

  useEffect(() => {
    if (open) {
      loadProfile();
    }
  }, [open]);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erro ao carregar perfil",
          description: "Usuário não autenticado",
          variant: "destructive",
        });
        return;
      }

      console.log('Carregando perfil do usuário:', user.id);

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('logo_url, full_name, location, linkedin_url, email_notifications_enabled')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Erro ao carregar perfil:', error);
        throw error;
      }

      const { data: candidate } = await supabase
        .from('candidates')
        .select('bio, skills, headline, experience_level, github_url, portfolio_url, instagram_url')
        .eq('profile_id', user.id)
        .maybeSingle();

      console.log('Perfil carregado:', profile);
      console.log('Candidato carregado:', candidate);

      if (profile) {
        setDescription(candidate?.bio || "");
        setFullName(profile.full_name || "");
        setHeadline(candidate?.headline || "");
        setLocation(profile.location || "");
        setExperienceLevel(candidate?.experience_level || "");
        setLinkedinUrl(profile.linkedin_url || "");
        setGithubUrl(candidate?.github_url || "");
        setPortfolioUrl(candidate?.portfolio_url || "");
        setInstagramUrl(candidate?.instagram_url || "");
        setSkills(Array.isArray(candidate?.skills) ? candidate.skills.map(String) : []);
        setPhotoUrl(profile.logo_url);
        setEmailNotifications(profile.email_notifications_enabled ?? true);
        setPhotoUrl(profile.logo_url);
      }
    } catch (error) {
      console.error('Erro ao carregar perfil:', error);
      toast({
        title: "Erro ao carregar perfil",
        description: "Ocorreu um erro ao carregar suas informações",
        variant: "destructive",
      });
    }
  };

  const handleSave = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Erro ao salvar perfil",
          description: "Usuário não autenticado",
          variant: "destructive",
        });
        return;
      }

      const updatesProfile = {
        id: user.id,
        logo_url: photoUrl,
        full_name: fullName,
        location: location,
        linkedin_url: linkedinUrl,
        email_notifications_enabled: emailNotifications,
        updated_at: new Date().toISOString(),
      };

      const updatesCandidate = {
        bio: description,
        skills: skills,
        headline: headline,
        experience_level: experienceLevel,
        github_url: githubUrl,
        portfolio_url: portfolioUrl,
        instagram_url: instagramUrl,
      };

      const { error } = await supabase
        .from('profiles')
        .update(updatesProfile)
        .eq('id', user.id);

      if (error) {
        console.error('Erro ao salvar perfil:', error);
        throw error;
      }

      console.log('Perfil atualizado com sucesso');

      const { error: candidateError } = await supabase
        .from('candidates')
        .upsert(
          {
            profile_id: user.id,
            ...updatesCandidate,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'profile_id' }
        );

      if (candidateError) {
        console.error('Erro ao salvar candidato:', candidateError);
        throw candidateError;
      }

      toast({
        title: "Perfil atualizado",
        description: "Suas informações foram atualizadas com sucesso",
      });

      // Invalida o cache do perfil para atualizar todos os componentes
      invalidateProfile();
      
      onOpenChange(false);
    } catch (error: any) {
      console.error('Erro ao salvar perfil:', error);
      const errorMessage = error?.message || error?.details || "Ocorreu um erro ao salvar suas informações";
      toast({
        title: "Erro ao salvar perfil",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Perfil</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <PhotoUploadField
            currentPhotoUrl={photoUrl}
            onPhotoChange={setPhotoUrl}
          />

          <ProfileBasicInfo
            fullName={fullName}
            headline={headline}
            location={location}
            experienceLevel={experienceLevel}
            onFullNameChange={setFullName}
            onHeadlineChange={setHeadline}
            onLocationChange={setLocation}
            onExperienceLevelChange={setExperienceLevel}
          />

          <ProfileBio
            description={description}
            onDescriptionChange={setDescription}
          />

          <div className="grid gap-2">
            <SkillsInput
              skills={skills}
              onSkillsChange={setSkills}
            />
          </div>

          <ProfileLinks
            linkedinUrl={linkedinUrl}
            githubUrl={githubUrl}
            portfolioUrl={portfolioUrl}
            instagramUrl={instagramUrl}
            onLinkedinUrlChange={setLinkedinUrl}
            onGithubUrlChange={setGithubUrl}
            onPortfolioUrlChange={setPortfolioUrl}
            onInstagramUrlChange={setInstagramUrl}
          />

          {/* Email Notifications Toggle */}
          <div className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <div className="space-y-0.5">
                <Label htmlFor="email-notifications" className="text-sm font-medium">
                  Notificações por email
                </Label>
                <p className="text-xs text-muted-foreground">
                  Receba alertas de novas vagas, vídeos e anúncios
                </p>
              </div>
            </div>
            <Switch
              id="email-notifications"
              checked={emailNotifications}
              onCheckedChange={setEmailNotifications}
            />
          </div>
        </div>
        <div className="flex justify-end gap-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSave}>
            Salvar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}